import { FormEvent, useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtomValue } from 'jotai/react';
import { useTranslation } from 'next-i18next';
import ImgGuidelineModeration from 'src/assets/images/imgGuidelineModeration.svg';
import ImgGuidelineNoConsent from 'src/assets/images/imgGuidelineNoConsent.svg';
import ImgGuidelineNoEntry from 'src/assets/images/imgGuidelineNoEntry.svg';
import ImgGuidelineSupport from 'src/assets/images/imgGuidelineSupport.svg';
import { tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { isRegisterLoadingAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import { setTargetBlankToLinks } from 'src/utils/html';
import { Container as ContainerBase, NextButton, NoticeText as NoticeTextBase, Section, Title as TitleBase, LoadingIndicator } from './styles';
import type { StepProps } from './types';
const GUIDELINES = [{
  key: 'real_time_ai_moderation_is_on',
  icon: ImgGuidelineModeration,
  title: 'guidelines_list_title_1',
  description: 'guidelines_list_desc_1'
}, {
  key: 'no_consent_strictly_prohibited',
  icon: ImgGuidelineNoConsent,
  title: 'guidelines_list_title_2',
  description: 'guidelines_list_desc_2'
}, {
  key: 'reports_are_reviewed_round_the_clock',
  icon: ImgGuidelineSupport,
  title: 'guidelines_list_title_3',
  description: 'guidelines_list_desc_3'
}, {
  key: 'avoid_showing_content_that_contains_minors',
  icon: ImgGuidelineNoEntry,
  title: 'guidelines_list_title_4',
  description: 'guidelines_list_desc_4'
}] as const;
const Container = styled(ContainerBase)`
  min-height: unset;

  ${({
  theme
}) => theme.screenSize.desktop} {
    gap: 8px;
  }
`;
const Title = styled(TitleBase)`
  margin-block-end: 32px;

  ${({
  theme
}) => theme.screenSize.desktop} {
    margin-block-end: unset;

    font-size: 40px;
    line-height: 56px;
  }
`;
const NoticeText = styled(NoticeTextBase)`
  ${({
  theme
}) => theme.screenSize.tablet} {
    display: none;
  }
`;
const TitleSection = styled(Section)`
  --gap: 8px;
`;
const OnboardingContainer = styled(Section)`
  flex: 1;

  padding: 0;
  min-height: 0;

  --gap: 30px;

  ${({
  theme
}) => theme.screenSize.desktop} {
    padding: 0;
    --gap: 32px;
  }
`;
const GuidelineItem = styled.div`
  display: flex;
  gap: 16px;
`;
const GuidelineContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
`;
const GuidelineIcon = styled.svg`
  flex-shrink: 0;

  width: 44px;
  height: 44px;
`;
const GuidelineTitle = styled.h2`
  margin: 0;

  color: ${({
  theme
}) => theme.color.content12};

  font-size: 20px;
  font-weight: 900;
  line-height: 26px;
`;
const GuidelineDescription = styled.p`
  color: ${({
  theme
}) => theme.color.content10};

  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
`;
const AdditionalConsentsWrapper = styled.div`
  margin-block-start: auto;

  display: flex;
  flex-direction: column;
  gap: 4px;

  padding: 14px 16px;
  border-radius: 6px;

  background-color: ${({
  theme
}) => theme.color.surface2};
`;
const AdditionalConsentsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;

  color: ${({
  theme
}) => theme.color.content10};

  a {
    text-decoration: underline;
    font-weight: 700;
    :visited {
      color: inherit;
    }
  }
`;
const AdditionalConsents = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  word-break: keep-all;

  color: ${({
  theme
}) => theme.color.content9};

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 12px;
    line-height: 18px;
  }

  a {
    color: ${({
  theme
}) => theme.color.content9};
  }
`;
const ButtonSection = styled(Section)`
  ${({
  theme
}) => theme.screenSize.desktop} {
    flex-direction: column-reverse;
  }
`;
export const CommunityGuidelineText = styled.p`
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${({
  theme
}) => theme.color.content9};

  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  a {
    color: inherit;
    :visited {
      color: inherit;
    }
  }
`;
export default function OnboardingStep({
  onNext,
  className
}: StepProps<void>) {
  const {
    t
  } = useTranslation();
  const tryLoginType = useAtomValue(tryLoginTypeAtom);
  const isRegisterLoading = useAtomValue(isRegisterLoadingAtom);
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onNext();
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_COMMUNITY,
      eventParams: {
        loginType: tryLoginType
      }
    });
  }, [tryLoginType]);
  return <Container as='form' onSubmit={handleSubmit} className={className} data-sentry-element="Container" data-sentry-component="OnboardingStep" data-sentry-source-file="Onboarding.tsx">
      <TitleSection data-sentry-element="TitleSection" data-sentry-source-file="Onboarding.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="Onboarding.tsx">{t('guidelines_title')}</Title>
        <NoticeText data-sentry-element="NoticeText" data-sentry-source-file="Onboarding.tsx">{t('guidelines_desc')}</NoticeText>
      </TitleSection>
      <OnboardingContainer data-sentry-element="OnboardingContainer" data-sentry-source-file="Onboarding.tsx">
        {GUIDELINES.map(({
        key,
        icon: IconComponent,
        title,
        description
      }) => <GuidelineItem key={key}>
            <GuidelineIcon as={IconComponent} />
            <GuidelineContent>
              <GuidelineTitle>{t(title)}</GuidelineTitle>
              <GuidelineDescription>{t(description)}</GuidelineDescription>
            </GuidelineContent>
          </GuidelineItem>)}
        <AdditionalConsentsWrapper data-sentry-element="AdditionalConsentsWrapper" data-sentry-source-file="Onboarding.tsx">
          <AdditionalConsentsTitle data-sentry-element="AdditionalConsentsTitle" data-sentry-source-file="Onboarding.tsx">{t('guidelines_liquidity_title')}</AdditionalConsentsTitle>
          <AdditionalConsents dangerouslySetInnerHTML={{
          __html: t('guidelines_liquidity_desc')
        }} data-sentry-element="AdditionalConsents" data-sentry-source-file="Onboarding.tsx" />
        </AdditionalConsentsWrapper>
      </OnboardingContainer>
      <ButtonSection data-sentry-element="ButtonSection" data-sentry-source-file="Onboarding.tsx">
        <NextButton type='submit' disabled={isRegisterLoading} data-sentry-element="NextButton" data-sentry-source-file="Onboarding.tsx">
          {isRegisterLoading ? <LoadingIndicator /> : t('guidelines_button')}
        </NextButton>
        <CommunityGuidelineText dangerouslySetInnerHTML={{
        __html: setTargetBlankToLinks(t('community_popup_bottomlink'))
      }} data-sentry-element="CommunityGuidelineText" data-sentry-source-file="Onboarding.tsx" />
      </ButtonSection>
    </Container>;
}